import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiClient } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Loader2 } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import EducationRequirements, { EDUCATION_CONFIG } from "@/components/EducationRequirements";
import { formatEducationForDisplay } from "@/utils/formatters";

// Helper to get field descriptions for education output
const getEducationFieldDescriptions = (education) => {
  if (!education || education.track !== "general_academic") return [];
  
  const descriptions = [];
  const allFields = new Set([
    ...(education.required?.fields || []),
    ...(education.preferred?.fields || [])
  ]);
  
  allFields.forEach(fieldValue => {
    if (fieldValue === "related_field") return; // Skip "related field"
    const field = EDUCATION_CONFIG.general_academic.fields.find(f => f.value === fieldValue);
    if (field) {
      descriptions.push({
        field: field.label,
        description: field.description
      });
    }
  });
  
  return descriptions;
};

export default function RequisitionBuilder() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [editId, setEditId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasRegenerated, setHasRegenerated] = useState(false);
  const [formData, setFormData] = useState({
    requisition_name: "",
    job_title: "",
    basic_qualifications: "",
    preferred_qualifications: "",
    education: null, // Now stores structured education object
    years_experience: "",
    location: "",
    company_brand: "",
    job_type: "Salaried",
    industry: "",
    tone: "",
    company_culture: "",
    additional_context: "",
    travel: "",
    relocation_provided: "",
    us_citizenship: [],
    security_clearance: "",
    shift_coverage: "",
    benefits_offered: []
  });
  const [generatedData, setGeneratedData] = useState(null);
  const [marketData, setMarketData] = useState(null);
  const [competitiveIntel, setCompetitiveIntel] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [enhancedResearch, setEnhancedResearch] = useState(false);
  const [progressStatus, setProgressStatus] = useState({ percent: 0, message: "" });

  // Extract editId from URL params with proper timing
  useEffect(() => {
    const id = searchParams.get('edit');
    console.log("URL params loaded - edit param:", id);
    setEditId(id);
  }, [searchParams]);

  // Load requisition data if in edit mode
  useEffect(() => {
    if (editId) {
      loadRequisitionForEdit(editId);
    } else {
      // Clear all form fields for new requisition
      setFormData({
        requisition_name: "",
        job_title: "",
        basic_qualifications: "",
        preferred_qualifications: "",
        education: null,
        years_experience: "",
        location: "",
        company_brand: "",
        job_type: "Salaried",
        additional_context: ""
      });
      setGeneratedData(null);
      setMarketData(null);
    }
  }, [editId]);

  const loadRequisitionForEdit = async (id) => {
    try {
      const response = await apiClient.get(`/requisitions/${id}`);
      const req = response.data;
      
      console.log("Loading requisition for edit:", req);
      console.log("Original inputs:", req.original_inputs);
      
      setIsEditMode(true);
      
      // Try to restore original inputs if available
      const originalInputs = req.original_inputs || {};
      
      // Get structured education data (education_details stores the structured object)
      const structuredEducation = originalInputs.education || req.education_details || null;
      
      const restoredFormData = {
        requisition_name: req.name || req.title,
        job_title: originalInputs.job_title || req.title,
        basic_qualifications: originalInputs.basic_qualifications || req.basic_qualifications || "",
        preferred_qualifications: originalInputs.preferred_qualifications || req.preferred_qualifications || "",
        education: structuredEducation,
        years_experience: originalInputs.years_experience || req.experience_years || "",
        location: originalInputs.location || req.location || "",
        company_brand: originalInputs.company_brand || "",
        job_type: originalInputs.job_type || req.job_type || "Salaried",
        industry: originalInputs.industry || "",
        tone: originalInputs.tone || "",
        company_culture: originalInputs.company_culture || "",
        additional_context: originalInputs.additional_context || "",
        travel: originalInputs.travel || "",
        relocation_provided: originalInputs.relocation_provided || "",
        us_citizenship: originalInputs.us_citizenship || [],
        security_clearance: originalInputs.security_clearance || "",
        shift_coverage: originalInputs.shift_coverage || "",
        benefits_offered: originalInputs.benefits_offered || []
      };
      
      console.log("Restored form data:", restoredFormData);
      console.log("Structured education:", structuredEducation);
      setFormData(restoredFormData);
      
      // Set generated data to show previous requisition for reference
      // But hasRegenerated stays false so save button doesn't appear until user regenerates
      setGeneratedData({
        title: req.title,
        description: req.description,
        requirements: req.requirements || [],
        skills: req.skills || [],
        preferred_skills: req.preferred_skills || [],
        location_benefits: req.location_benefits || "",
        basic_qualifications: req.basic_qualifications || "",
        preferred_qualifications: req.preferred_qualifications || "",
        education: req.education || "",
        job_type: req.job_type || "Salaried",
        experience_years: req.experience_years || "",
        salary_range: req.salary_range || "",
        location: req.location || ""
      });
      setHasRegenerated(false); // No save button until user regenerates
      
      // Load market data if available (for reference)
      if (req.market_data) {
        setMarketData(req.market_data);
      }
      
      // Load competitive intelligence if available and set Enhanced Research
      if (req.competitive_intelligence) {
        setCompetitiveIntel(req.competitive_intelligence);
        setEnhancedResearch(true); // Pre-check Enhanced Research if CI exists
        console.log("Loaded competitive intelligence from saved requisition");
      }
      
      toast.success("Requisition loaded for editing - modify inputs and generate updated version");
    } catch (error) {
      console.error("Failed to load requisition:", error);
      toast.error("Failed to load requisition");
      navigate("/dashboard");
    }
  };

  const handleGenerate = async () => {
    if (!formData.job_title) {
      toast.error("Please enter a job title");
      return;
    }

    // Check for duplicate requisition name before generating (only for new requisitions)
    if (!isEditMode && formData.requisition_name && formData.requisition_name.trim()) {
      try {
        const response = await apiClient.get("/requisitions");
        const existingReqs = response.data;
        const duplicateName = existingReqs.some(
          req => req.name.toLowerCase() === formData.requisition_name.trim().toLowerCase()
        );
        
        if (duplicateName) {
          toast.error(
            <div>
              <strong>Duplicate Name</strong>
              <p className="text-sm mt-1">A requisition with this name already exists.</p>
              <p className="text-sm mt-1">Please change the <strong>Requisition Name</strong> field and try again.</p>
            </div>,
            { duration: 6000 }
          );
          return;
        }
      } catch (error) {
        console.error("Error checking duplicate names:", error);
        // Continue with generation even if check fails
      }
    }

    setGenerating(true);
    setShowPreview(false); // Ensure showPreview is false for Generate
    setProgressStatus({ percent: 0, message: "" });
    
    try {
      // Progress updates
      setProgressStatus({ percent: 10, message: "Initializing AI analysis..." });
      await new Promise(resolve => setTimeout(resolve, 300));
      
      setProgressStatus({ percent: 20, message: "Analyzing job requirements..." });
      await new Promise(resolve => setTimeout(resolve, 300));
      
      if (enhancedResearch) {
        setProgressStatus({ percent: 30, message: "Searching authoritative sources (O*NET, BLS)..." });
        await new Promise(resolve => setTimeout(resolve, 500));
        
        setProgressStatus({ percent: 45, message: "Extracting professional standards and definitions..." });
        await new Promise(resolve => setTimeout(resolve, 500));
      } else {
        setProgressStatus({ percent: 35, message: "Processing input data..." });
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      setProgressStatus({ percent: 55, message: "Fetching competitive market intelligence..." });
      await new Promise(resolve => setTimeout(resolve, 400));
      
      setProgressStatus({ percent: 70, message: "Analyzing similar job postings..." });
      await new Promise(resolve => setTimeout(resolve, 400));
      
      setProgressStatus({ percent: 80, message: "Generating requisition content..." });
      
      // Convert structured education to display string for API
      const educationForApi = formatEducationForDisplay(formData.education);
      
      // Generate requisition with enhanced research flag
      const response = await apiClient.post("/requisitions/generate", {
        ...formData,
        education: educationForApi, // Send formatted string to API
        education_details: formData.education, // Also send structured data
        enhanced_research: enhancedResearch
      });
      
      setProgressStatus({ percent: 90, message: "Fetching salary data..." });
      
      if (response.data.success) {
        setGeneratedData(response.data.requisition);
        
        // Mark as regenerated if in edit mode
        if (isEditMode) {
          setHasRegenerated(true);
        }
        
        // Store competitive intelligence
        if (response.data.competitive_intelligence) {
          setCompetitiveIntel(response.data.competitive_intelligence);
        }
        
        // Set market data from the main response (if available)
        if (response.data.market_data) {
          setMarketData(response.data.market_data);
          console.log("Market data from main response:", response.data.market_data);
        } else {
          // Fallback: Fetch market data with salary analysis from separate endpoint
          try {
            const marketResponse = await apiClient.post("/market/analyze", formData);
            if (marketResponse.data.success) {
              setMarketData(marketResponse.data.data);
              console.log("Market data from separate endpoint:", marketResponse.data.data);
            }
          } catch (marketError) {
            console.error("Market analysis error:", marketError);
            // Don't fail the whole generation if market data fails
          }
        }
        
        setProgressStatus({ percent: 100, message: "Complete!" });
        await new Promise(resolve => setTimeout(resolve, 500));
        setProgressStatus({ percent: 0, message: "" });
        toast.success("Requisition generated successfully!");
      }
    } catch (error) {
      console.error("Generation error:", error);
      toast.error("Failed to generate requisition");
      setProgressStatus({ percent: 0, message: "" });
    } finally {
      setGenerating(false);
    }
  };

  const handlePreview = async () => {
    if (!formData.job_title) {
      toast.error("Please enter a job title");
      return;
    }

    // Check for duplicate requisition name before previewing (only for new requisitions)
    if (!isEditMode && formData.requisition_name && formData.requisition_name.trim()) {
      try {
        const response = await apiClient.get("/requisitions");
        const existingReqs = response.data;
        const duplicateName = existingReqs.some(
          req => req.name.toLowerCase() === formData.requisition_name.trim().toLowerCase()
        );
        
        if (duplicateName) {
          toast.error(
            <div>
              <strong>Duplicate Name</strong>
              <p className="text-sm mt-1">A requisition with this name already exists.</p>
              <p className="text-sm mt-1">Please change the <strong>Requisition Name</strong> field and try again.</p>
            </div>,
            { duration: 6000 }
          );
          return;
        }
      } catch (error) {
        console.error("Error checking duplicate names:", error);
        // Continue with preview even if check fails
      }
    }

    setGenerating(true);
    setShowPreview(true);
    setProgressStatus({ percent: 0, message: "" });
    
    try {
      // Clear market analysis data for preview
      setMarketData(null);
      
      // Preview requisition (same as generate but explicitly for preview)
      const response = await apiClient.post("/requisitions/preview", formData);
      if (response.data.success) {
        setGeneratedData(response.data.requisition);
        
        // Store competitive intelligence
        if (response.data.competitive_intelligence) {
          setCompetitiveIntel(response.data.competitive_intelligence);
        }
        
        toast.success("Preview generated!");
      }
    } catch (error) {
      console.error("Preview error:", error);
      toast.error("Failed to generate preview");
    } finally {
      setGenerating(false);
      setProgressStatus({ percent: 0, message: "" });
    }
  };

  const handleSave = async () => {
    if (!generatedData) return;
    
    if (!formData.requisition_name || formData.requisition_name.trim() === "") {
      toast.error("Please enter a name for this requisition");
      return;
    }

    setSaving(true);
    try {
      // Check for duplicate requisition name
      const response = await apiClient.get("/requisitions");
      const existingReqs = response.data;
      const duplicateName = existingReqs.some(
        req => req.name.toLowerCase() === formData.requisition_name.trim().toLowerCase() 
          && req.id !== editId // Allow same name for current requisition being edited
      );
      
      if (duplicateName) {
        toast.error(
          <div>
            <strong>Duplicate Name</strong>
            <p className="text-sm mt-1">A requisition with this name already exists.</p>
            <p className="text-sm mt-1">Please change the <strong>Requisition Name</strong> field and try again.</p>
          </div>,
          { duration: 6000 }
        );
        setSaving(false);
        return;
      }
      
      // Convert structured education to display string for storage
      const educationDisplay = formatEducationForDisplay(formData.education);
      
      // Prepare original inputs for future editing (store structured data)
      const originalInputs = {
        job_title: formData.job_title,
        basic_qualifications: formData.basic_qualifications,
        preferred_qualifications: formData.preferred_qualifications,
        education: formData.education, // Store structured data for editing
        years_experience: formData.years_experience,
        location: formData.location,
        company_brand: formData.company_brand,
        job_type: formData.job_type,
        industry: formData.industry,
        tone: formData.tone,
        company_culture: formData.company_culture,
        additional_context: formData.additional_context,
        travel: formData.travel,
        relocation_provided: formData.relocation_provided,
        us_citizenship: formData.us_citizenship,
        security_clearance: formData.security_clearance,
        shift_coverage: formData.shift_coverage,
        benefits_offered: formData.benefits_offered
      };
      
      if (isEditMode && editId) {
        // Update existing requisition
        await apiClient.put(`/requisitions/${editId}`, {
          name: formData.requisition_name,
          title: generatedData.title,
          description: generatedData.description,
          requirements: generatedData.requirements,
          skills: generatedData.skills,
          preferred_skills: generatedData.preferred_skills || [],
          location_benefits: generatedData.location_benefits || "",
          basic_qualifications: generatedData.basic_qualifications || "",
          preferred_qualifications: generatedData.preferred_qualifications || "",
          education: generatedData.education || educationDisplay,
          education_details: formData.education, // Store structured education data
          job_type: generatedData.job_type || formData.job_type,
          experience_years: generatedData.experience_years,
          salary_range: generatedData.salary_range,
          location: generatedData.location,
          company_brand: formData.company_brand || "",
          travel: formData.travel || "",
          relocation_provided: formData.relocation_provided || "",
          us_citizenship: formData.us_citizenship || [],
          security_clearance: formData.security_clearance || "",
          shift_coverage: formData.shift_coverage || "",
          benefits_offered: formData.benefits_offered || [],
          market_data: marketData || null,
          competitive_intelligence: competitiveIntel || null,
          original_inputs: originalInputs
        });
        toast.success("Requisition updated successfully!");
      } else {
        // Create new requisition
        await apiClient.post("/requisitions", {
          name: formData.requisition_name,
          title: generatedData.title,
          description: generatedData.description,
          requirements: generatedData.requirements,
          skills: generatedData.skills,
          preferred_skills: generatedData.preferred_skills || [],
          location_benefits: generatedData.location_benefits || "",
          basic_qualifications: generatedData.basic_qualifications || "",
          preferred_qualifications: generatedData.preferred_qualifications || "",
          education: generatedData.education || educationDisplay,
          education_details: formData.education, // Store structured education data
          job_type: generatedData.job_type || formData.job_type,
          experience_years: generatedData.experience_years,
          salary_range: generatedData.salary_range,
          location: generatedData.location,
          company_brand: formData.company_brand || "",
          travel: formData.travel || "",
          relocation_provided: formData.relocation_provided || "",
          us_citizenship: formData.us_citizenship || [],
          security_clearance: formData.security_clearance || "",
          shift_coverage: formData.shift_coverage || "",
          benefits_offered: formData.benefits_offered || [],
          market_data: marketData || null,
          competitive_intelligence: competitiveIntel || null,
          original_inputs: originalInputs
        });
        toast.success("Requisition saved successfully!");
      }
      navigate("/dashboard");
    } catch (error) {
      console.error("Save error:", error);
      toast.error(isEditMode ? "Failed to update requisition" : "Failed to save requisition");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              // Navigate to dashboard/desktop
              navigate("/dashboard");
            }}
            data-testid={editId ? "return-to-req-desktop-btn" : "back-to-dashboard-btn"}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {editId ? "Return to Req Desktop" : "Back"}
          </Button>
          {editId && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                // Navigate directly to the requisition detail page
                navigate(`/requisition/${editId}`);
              }}
              data-testid="return-to-req-view-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Return to Req View
            </Button>
          )}
          <h1 className="text-xl font-bold text-slate-900">
            {isEditMode ? "Edit Requisition Input" : "Create Job Requisition"}
          </h1>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Form */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Job Details</CardTitle>
                <CardDescription>Tell us about the position you want to fill</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="job_title">Job Title</Label>
                  <Input
                    id="job_title"
                    placeholder="e.g., Senior Software Engineer"
                    value={formData.job_title}
                    onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                    data-testid="job-title-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="requisition_name">Requisition Name</Label>
                  <Input
                    id="requisition_name"
                    placeholder="e.g., Senior Engineer - Bay Area Q4 2025"
                    value={formData.requisition_name}
                    onChange={(e) => setFormData({ ...formData, requisition_name: e.target.value })}
                    data-testid="requisition-name-input"
                  />
                  <p className="text-xs text-slate-500">Give this requisition a unique name for easy reference</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company_brand">Company Brand</Label>
                  <Input
                    id="company_brand"
                    placeholder="e.g., Acme Inc."
                    value={formData.company_brand}
                    onChange={(e) => setFormData({ ...formData, company_brand: e.target.value })}
                    data-testid="company-brand-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="basic_qualifications">Basic Qualifications</Label>
                  <Textarea
                    id="basic_qualifications"
                    placeholder="e.g., 5 years experience, required skills & experience, minimum certification(s), etc."
                    value={formData.basic_qualifications}
                    onChange={(e) => setFormData({ ...formData, basic_qualifications: e.target.value })}
                    rows={3}
                    data-testid="basic-qualifications-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="preferred_qualifications">Preferred Qualifications</Label>
                  <Textarea
                    id="preferred_qualifications"
                    placeholder="e.g., Ideal skills & relevant experience, proficiencies, desired certifications, etc."
                    value={formData.preferred_qualifications}
                    onChange={(e) => setFormData({ ...formData, preferred_qualifications: e.target.value })}
                    rows={3}
                    data-testid="preferred-qualifications-input"
                  />
                </div>

                <div className="space-y-2">
                  <EducationRequirements
                    value={formData.education}
                    onChange={(educationData) => setFormData({ ...formData, education: educationData })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="years_experience">Years of Experience</Label>
                  <Input
                    id="years_experience"
                    placeholder="e.g., 3-5 years"
                    value={formData.years_experience}
                    onChange={(e) => setFormData({ ...formData, years_experience: e.target.value })}
                    data-testid="years-experience-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    placeholder="e.g., San Francisco, CA or Remote"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    data-testid="location-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="industry">Industry</Label>
                  <Select
                    value={formData.industry}
                    onValueChange={(value) => setFormData({ ...formData, industry: value })}
                  >
                    <SelectTrigger id="industry">
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Technology/Software">Technology/Software</SelectItem>
                      <SelectItem value="Healthcare/Biotech">Healthcare/Biotech</SelectItem>
                      <SelectItem value="Finance/Banking">Finance/Banking</SelectItem>
                      <SelectItem value="Manufacturing/Industrial">Manufacturing/Industrial</SelectItem>
                      <SelectItem value="Aerospace/Defense">Aerospace/Defense</SelectItem>
                      <SelectItem value="Retail/E-commerce">Retail/E-commerce</SelectItem>
                      <SelectItem value="Education">Education</SelectItem>
                      <SelectItem value="Government/Public Sector">Government/Public Sector</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tone">Tone & Style</Label>
                  <Select
                    value={formData.tone}
                    onValueChange={(value) => setFormData({ ...formData, tone: value })}
                  >
                    <SelectTrigger id="tone">
                      <SelectValue placeholder="Select writing tone" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Professional">Professional (formal, corporate)</SelectItem>
                      <SelectItem value="Innovative">Innovative (dynamic, forward-thinking)</SelectItem>
                      <SelectItem value="Collaborative">Collaborative (team-focused)</SelectItem>
                      <SelectItem value="Entrepreneurial">Entrepreneurial (startup-oriented)</SelectItem>
                      <SelectItem value="Production">Production (deliverable-driven)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company_culture">Company Culture</Label>
                  <Textarea
                    id="company_culture"
                    placeholder="Describe your company culture, values, and work environment..."
                    value={formData.company_culture}
                    onChange={(e) => setFormData({ ...formData, company_culture: e.target.value })}
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="additional_context">Additional Context: Suggestions Below</Label>
                  <Textarea
                    id="additional_context"
                    placeholder="Any specific requirements, team details, or context..."
                    value={formData.additional_context}
                    onChange={(e) => setFormData({ ...formData, additional_context: e.target.value })}
                    rows={4}
                    data-testid="context-textarea"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="travel">Travel</Label>
                  <Select
                    value={formData.travel}
                    onValueChange={(value) => setFormData({ ...formData, travel: value })}
                  >
                    <SelectTrigger id="travel">
                      <SelectValue placeholder="Select travel requirement" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="No travel">No travel</SelectItem>
                      <SelectItem value="Up to 25%">Up to 25%</SelectItem>
                      <SelectItem value="Up to 50%">Up to 50%</SelectItem>
                      <SelectItem value="Up to 75%">Up to 75%</SelectItem>
                      <SelectItem value="Up to 100%">Up to 100%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Additional Details Section */}
                <Card className="bg-slate-50">
                  <CardHeader className="pb-3">
                    <Label className="text-sm font-medium">Additional Details</Label>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="relocation_provided">Relocation Provided</Label>
                      <Select
                        value={formData.relocation_provided}
                        onValueChange={(value) => setFormData({ ...formData, relocation_provided: value })}
                      >
                        <SelectTrigger id="relocation_provided">
                          <SelectValue placeholder="Select option" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Yes">Yes</SelectItem>
                          <SelectItem value="No">No</SelectItem>
                          <SelectItem value="Negotiable">Negotiable</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>US Citizenship (Check all that apply)</Label>
                      <div className="space-y-2 mt-2">
                        {["None", "U.S. Citizen", "U.S. permanent resident (i.e. Green Card holder)", "Lawfully admitted into U.S. as a refugee or granted asylum"].map((option) => {
                          const currentCitizenship = formData.us_citizenship || [];
                          const noneSelected = currentCitizenship.includes("None");
                          const othersSelected = currentCitizenship.some(item => item !== "None");
                          const isDisabled = (option === "None" && othersSelected) || (option !== "None" && noneSelected);
                          
                          return (
                            <div key={option} className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                id={`citizenship-${option}`}
                                checked={currentCitizenship.includes(option)}
                                disabled={isDisabled}
                                onChange={(e) => {
                                  const newCitizenship = e.target.checked
                                    ? [...currentCitizenship, option]
                                    : currentCitizenship.filter(item => item !== option);
                                  setFormData({ ...formData, us_citizenship: newCitizenship });
                                }}
                                className={`w-4 h-4 rounded border-gray-300 ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                              />
                              <label 
                                htmlFor={`citizenship-${option}`} 
                                className={`text-sm cursor-pointer ${isDisabled ? 'text-slate-400' : 'text-slate-700'}`}
                              >
                                {option}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="security_clearance">Security Clearance Requirements</Label>
                      <Select
                        value={formData.security_clearance}
                        onValueChange={(value) => setFormData({ ...formData, security_clearance: value })}
                      >
                        <SelectTrigger id="security_clearance">
                          <SelectValue placeholder="Select clearance level" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="None">None Required</SelectItem>
                          <SelectItem value="Confidential">Confidential</SelectItem>
                          <SelectItem value="Secret">Secret</SelectItem>
                          <SelectItem value="Top Secret">Top Secret</SelectItem>
                          <SelectItem value="TS/SCI">TS/SCI</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="job_type">Job Type</Label>
                      <Select
                        value={formData.job_type}
                        onValueChange={(value) => setFormData({ ...formData, job_type: value })}
                      >
                        <SelectTrigger id="job_type" data-testid="job-type-select">
                          <SelectValue placeholder="Select job type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Salaried">Salaried</SelectItem>
                          <SelectItem value="Hourly">Hourly</SelectItem>
                          <SelectItem value="Part Time">Part Time</SelectItem>
                          <SelectItem value="Contract">Contract</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="shift_coverage">Shift Coverage</Label>
                      <Select
                        value={formData.shift_coverage}
                        onValueChange={(value) => setFormData({ ...formData, shift_coverage: value })}
                      >
                        <SelectTrigger id="shift_coverage">
                          <SelectValue placeholder="Select shift" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="First Shift">First Shift (Day)</SelectItem>
                          <SelectItem value="Second Shift">Second Shift (Evening)</SelectItem>
                          <SelectItem value="Third Shift">Third Shift (Night)</SelectItem>
                          <SelectItem value="Rotating">Rotating Shifts</SelectItem>
                          <SelectItem value="Flexible">Flexible</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Benefits Offered (Check all that apply)</Label>
                      <div className="space-y-2 mt-2">
                        {["Paid time off", "Paid holidays", "9/80 Work week", "Paid parental leave", "Health insurance", "Health savings account", "Vision insurance", "Dental insurance", "Life insurance", "Disability insurance", "401(k) matching", "Stock Purchase Plan", "Tuition reimbursement"].map((benefit) => (
                          <div key={benefit} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`benefit-${benefit}`}
                              checked={(formData.benefits_offered || []).includes(benefit)}
                              onChange={(e) => {
                                const currentBenefits = formData.benefits_offered || [];
                                const newBenefits = e.target.checked
                                  ? [...currentBenefits, benefit]
                                  : currentBenefits.filter(item => item !== benefit);
                                setFormData({ ...formData, benefits_offered: newBenefits });
                              }}
                              className="w-4 h-4 rounded border-gray-300"
                            />
                            <label htmlFor={`benefit-${benefit}`} className="text-sm text-slate-700 cursor-pointer">
                              {benefit}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="space-y-3">
                  {/* Enhanced Research Toggle */}
                  <div className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="enhanced-research"
                        checked={enhancedResearch}
                        onChange={(e) => setEnhancedResearch(e.target.checked)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="enhanced-research" className="font-semibold text-sm text-blue-900 cursor-pointer">
                        Enhanced Research
                      </label>
                    </div>
                    <button
                      type="button"
                      className="text-blue-600 hover:text-blue-800"
                      onClick={() => alert('Enhanced Research searches authoritative sources (O*NET, BLS, professional associations) for comprehensive job title definitions, standard duties, and industry requirements. This provides richer, more professional requisitions with 2-3 additional web searches per generation.')}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                  </div>
                  {enhancedResearch && (
                    <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <p className="text-xs text-emerald-800">
                        <strong>✓ Enhanced Research Enabled:</strong> AI will search authoritative sources including O*NET, BLS, and professional standards to provide comprehensive job definitions and requirements.
                      </p>
                    </div>
                  )}
                  
                  {/* Progress Indicator */}
                  {generating && !showPreview && progressStatus.message && (
                    <div className="p-4 bg-white border border-blue-200 rounded-lg space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-blue-900">{progressStatus.message}</span>
                        <span className="text-blue-600 font-semibold">{progressStatus.percent}%</span>
                      </div>
                      <Progress value={progressStatus.percent} className="h-2" />
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      onClick={handlePreview}
                      disabled={generating || !formData.job_title}
                      variant="outline"
                      className="w-full"
                    >
                      {generating && showPreview ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Previewing...
                        </>
                      ) : (
                        "Preview"
                      )}
                    </Button>
                    
                    <Button
                      onClick={handleGenerate}
                      disabled={generating || !formData.job_title}
                      className="w-full"
                      data-testid="generate-btn"
                    >
                      {generating && !showPreview ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          AI Working...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          {isEditMode ? "Generate Updated Req" : "Generate with AI"}
                        </>
                      )}
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                    <div className="px-2 py-2 bg-slate-50 rounded border border-slate-200">
                      <p className="font-medium text-slate-700 mb-1">Preview</p>
                      <p>Quick draft generation without market analysis. Use to test different inputs.</p>
                    </div>
                    <div className="px-2 py-2 bg-blue-50 rounded border border-blue-200">
                      <p className="font-medium text-blue-700 mb-1">Generate with AI</p>
                      <p>Full generation with competitive intelligence and market salary analysis.</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Generated Output */}
          <div className="space-y-6">
            {generatedData ? (
              <Card className="animate-fade-in">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>
                      <span className="font-bold">Requisition Name: </span>
                      {formData.requisition_name || "Generated Requisition"}
                    </CardTitle>
                    {/* Only show save button if: 
                        1. Not in edit mode, OR
                        2. In edit mode AND user has regenerated */}
                    {(!isEditMode || (isEditMode && hasRegenerated)) && (
                      <Button
                        onClick={handleSave}
                        disabled={saving}
                        data-testid="save-requisition-btn"
                      >
                        {saving ? (isEditMode ? "Updating..." : "Saving...") : (isEditMode ? "Update Requisition" : "Save Requisition")}
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4" data-testid="generated-content">
                  <div>
                    <p className="text-slate-900">
                      <span className="font-bold">Job Title: </span>
                      {generatedData.title}
                    </p>
                  </div>

                  {formData.company_brand && (
                    <div>
                      <p className="text-slate-900">
                        <span className="font-bold">Company Brand: </span>
                        {formData.company_brand}
                      </p>
                    </div>
                  )}

                  <div>
                    <h3 className="font-semibold text-sm text-slate-600 mb-1">Description</h3>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{generatedData.description}</p>
                  </div>

                  {generatedData.requirements && generatedData.requirements.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-sm text-slate-600 mb-2">Key Responsibilities</h3>
                      <ul className="list-disc list-outside space-y-2 pl-6">
                        {generatedData.requirements.filter(req => req && req.trim() && req !== '**').map((req, i) => (
                          <li key={i} className="text-sm text-slate-700 break-words">{req}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {generatedData.skills && generatedData.skills.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-sm text-slate-600 mb-2">Required Skills</h3>
                      <ul className="list-disc list-outside space-y-2 pl-6">
                        {generatedData.skills.filter(skill => skill && skill.trim() && skill !== '**').map((skill, i) => (
                          <li key={i} className="text-sm text-slate-700 break-words">
                            {skill}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {generatedData.preferred_skills && generatedData.preferred_skills.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-sm text-slate-600 mb-2">Preferred Skills</h3>
                      <ul className="list-disc list-outside space-y-2 pl-6">
                        {generatedData.preferred_skills.filter(skill => skill && skill.trim() && skill !== '**').map((skill, i) => (
                          <li key={i} className="text-sm text-slate-700 break-words">
                            {skill}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {generatedData.experience_years && (
                    <div>
                      <h3 className="font-semibold text-sm text-slate-600 mb-1">Experience</h3>
                      <div className="text-sm text-slate-700 whitespace-pre-line">{generatedData.experience_years}</div>
                    </div>
                  )}

                  {/* Education Section - Display the formatted education string */}
                  {(generatedData.education || formatEducationForDisplay(formData.education)) && (
                    <div>
                      <h3 className="font-semibold text-sm text-slate-600 mb-1">Education</h3>
                      <p className="text-sm text-slate-700">{generatedData.education || formatEducationForDisplay(formData.education)}</p>
                    </div>
                  )}

                  {generatedData.salary_range && (
                    <div>
                      <h3 className="font-semibold text-sm text-slate-600 mb-1">Salary Range</h3>
                      <p className="text-sm text-slate-700">{generatedData.salary_range}</p>
                    </div>
                  )}

                  {formData.travel && (
                    <div>
                      <h3 className="font-semibold text-sm text-slate-600 mb-1">Travel</h3>
                      <p className="text-sm text-slate-700">{formData.travel}</p>
                    </div>
                  )}

                  {/* Additional Details Section */}
                  {(formData.relocation_provided || formData.us_citizenship?.length > 0 || formData.security_clearance || formData.job_type || formData.shift_coverage || formData.benefits_offered?.length > 0) && (
                    <div>
                      <h3 className="font-semibold text-sm text-slate-600 mb-2">Additional Details</h3>
                      <div className="space-y-1">
                        {formData.relocation_provided && (
                          <p className="text-sm text-slate-700">
                            <span className="font-semibold">Relocation:</span> {formData.relocation_provided}
                          </p>
                        )}
                        {formData.us_citizenship && formData.us_citizenship.length > 0 && (
                          <p className="text-sm text-slate-700">
                            <span className="font-semibold">US Citizenship:</span> {formData.us_citizenship.join(", ")}
                          </p>
                        )}
                        {formData.security_clearance && (
                          <p className="text-sm text-slate-700">
                            <span className="font-semibold">Security Clearance:</span> {formData.security_clearance}
                          </p>
                        )}
                        {formData.job_type && (
                          <p className="text-sm text-slate-700">
                            <span className="font-semibold">Job Type:</span> {formData.job_type}
                          </p>
                        )}
                        {formData.shift_coverage && (
                          <p className="text-sm text-slate-700">
                            <span className="font-semibold">Shift:</span> {formData.shift_coverage}
                          </p>
                        )}
                        {formData.benefits_offered && formData.benefits_offered.length > 0 && (
                          <p className="text-sm text-slate-700">
                            <span className="font-semibold">Benefits Offered:</span> {formData.benefits_offered.join(", ")}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {generatedData.location_benefits && (
                    <div>
                      <h3 className="font-semibold text-sm text-slate-600 mb-1">Location</h3>
                      <p className="text-sm text-slate-700">{generatedData.location_benefits}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="h-full flex items-center justify-center">
                <CardContent className="text-center py-12">
                  <Sparkles className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">AI-Generated Content</h3>
                  <p className="text-sm text-slate-600">
                    Fill in the job details and click &quot;Generate with AI&quot; to create a professional job requisition
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Competitive Intelligence */}
            {competitiveIntel && Object.keys(competitiveIntel).length > 0 && (
              <Card className="animate-fade-in">
                <CardHeader>
                  <CardTitle>Competitive Intelligence</CardTitle>
                  <CardDescription>Insights from similar job postings in the market</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {competitiveIntel.in_demand_skills && competitiveIntel.in_demand_skills.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-sm text-slate-900 mb-2">Top In-Demand Skills</h3>
                      <div className="flex flex-wrap gap-2">
                        {competitiveIntel.in_demand_skills.map((skill, i) => (
                          <span key={i} className="text-xs px-3 py-1 bg-blue-50 text-blue-700 rounded-full border border-blue-200">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {competitiveIntel.common_requirements && competitiveIntel.common_requirements.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-sm text-slate-900 mb-2">Common Requirements</h3>
                      <ul className="space-y-1">
                        {competitiveIntel.common_requirements.map((req, i) => (
                          <li key={i} className="text-sm text-slate-700">• {req}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {competitiveIntel.typical_qualifications && (
                    <div>
                      <h3 className="font-semibold text-sm text-slate-900 mb-2">Typical Qualifications</h3>
                      <p className="text-sm text-slate-700">{competitiveIntel.typical_qualifications}</p>
                    </div>
                  )}
                  
                  {competitiveIntel.competitive_salary && (
                    <div>
                      <h3 className="font-semibold text-sm text-slate-900 mb-2">Competitive Salary Range</h3>
                      <p className="text-sm font-medium text-slate-900">{competitiveIntel.competitive_salary}</p>
                    </div>
                  )}
                  
                  {competitiveIntel.unique_perks && competitiveIntel.unique_perks.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-sm text-slate-900 mb-2">Unique Perks & Benefits</h3>
                      <ul className="space-y-1">
                        {competitiveIntel.unique_perks.map((perk, i) => (
                          <li key={i} className="text-sm text-slate-700">• {perk}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Market Data & Salary Analysis */}
            {marketData && (
              <Card className="animate-fade-in">
                <CardHeader>
                  <CardTitle>Market Analysis</CardTitle>
                  <CardDescription>Salary insights and market data from multiple sources</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Competitive Salary (New Structure) */}
                  {marketData.competitive_salary && (
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <h3 className="font-semibold text-sm text-slate-900 mb-2">Competitive Salary Range</h3>
                      <p className="text-lg font-medium text-blue-900 mb-2">{marketData.competitive_salary}</p>
                      {marketData.data_quality && (
                        <div className="flex items-center gap-3 text-xs">
                          <span className={`px-2 py-1 rounded-full font-semibold ${
                            marketData.data_quality.confidence === 'High' ? 'bg-green-100 text-green-700' :
                            marketData.data_quality.confidence === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {marketData.data_quality.confidence} Confidence
                          </span>
                          {marketData.data_quality.sample_count > 0 && (
                            <span className="text-slate-600">{marketData.data_quality.sample_count} data points</span>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Salary Analysis (Old Structure) */}
                  {marketData.salary_analysis && marketData.salary_analysis.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-sm text-slate-900 mb-3">Salary Analysis by Source</h3>
                      <div className="space-y-2">
                        {marketData.salary_analysis.map((source, i) => (
                          <div key={i} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-sm text-slate-900">{source.source}</span>
                              <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">{source.confidence}</span>
                            </div>
                            <p className="text-sm text-slate-700">{source.range}</p>
                            {source.details && (
                              <p className="text-xs text-slate-500 mt-1">{source.details}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Data Sources - support both old and new structure */}
                  {((marketData.data_quality?.sources && marketData.data_quality.sources.length > 0) || 
                    (marketData.sources_scraped && marketData.sources_scraped.length > 0)) && (
                    <div>
                      <h3 className="font-semibold text-sm text-slate-900 mb-2">Data Sources</h3>
                      <div className="flex flex-wrap gap-2">
                        {(marketData.data_quality?.sources || marketData.sources_scraped || []).map((source, i) => (
                          <span key={i} className="text-xs px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full border border-emerald-200">
                            {source}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Market Insights */}
                  {marketData.insights && (
                    <div>
                      <h3 className="font-semibold text-sm text-slate-900 mb-2">Market Insights</h3>
                      <p className="text-sm text-slate-700">{marketData.insights}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}